import './CoralMasterCard.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import { Icon } from '@coral-library/icons/Icon/Icon'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { CTA } from '@websrc/App/Components/CTA'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { CoralMasterCardVisuals, mapCoralMasterCard } from '@websrc/DataMappers/mapCoralMasterCard'
import React, { useEffect, useRef, useState } from 'react'
import AnimateHeight from 'react-animate-height'

import { CoralAttribute } from '../../../Attribute/CoralAttribute'
import { CoralBadge } from '../../../Badge/CoralBadge'

type CoralMasterCardProps = ReturnType<typeof mapCoralMasterCard> & {
  visuals?: CoralMasterCardVisuals
  onCTAClick?: () => void
}

// eslint-disable-next-line max-lines-per-function
export const CoralMasterCard: React.FC<CoralMasterCardProps> = (props) => {
  const singleCtaRef = useRef<any>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(null)
  const { visuals = {} } = props

  const hasContentAboveButtons =
    !!props.badges || !!props.subtitle || !!props.headline || !!props.body

  const hasContentAboveBody = !!props.badges || !!props.subtitle || !!props.headline
  if (!hasContentAboveBody) visuals.paddingBody = undefined

  const hasContentAboveHeadline = !!props.badges || !!props.subtitle
  if (!hasContentAboveHeadline) visuals.paddingHeadline = undefined

  const hasContentAboveSubtitle = !!props.badges
  if (!hasContentAboveSubtitle) visuals.paddingSubhead = undefined

  const triggerSingleCta = () => singleCtaRef.current?.click()
  const toggleAccordionItem = (index: number) => {
    const itemsLength = props.accordion?.items?.length ?? 0

    if (openAccordionIndex === index) {
      const nextIndex = (index + 1) % itemsLength
      setOpenAccordionIndex(nextIndex)
    } else {
      setOpenAccordionIndex(index)
    }
  }

  const isDesktop = useIsWindowBiggerThan(1079)

  useEffect(() => {
    setOpenAccordionIndex(isDesktop ? 0 : null)
  }, [isDesktop])

  //Set default visuals values here
  visuals.buttonTheme = 'white'

  useEffect(() => {
    const handleMouseUp = () => setIsPressed(false)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  const badges = props.badges
    .filter((badge) => badge.label?.trim())
    .map((badge, index) => (
      <CoralBadge
        absolutePosition={visuals?.badgeAbsolutePosition}
        backgroundColor={badge.color}
        fontSize={visuals?.badgeSize}
        key={index}
        radius={visuals?.radiusBadge}
        text={badge.label}
      />
    ))

  if (props.buttons && props.buttons.length > 0) {
    props.buttons.forEach((button) => {
      if (button.__ctaType === 'file') {
        button.__ctaType = 'button'
      }
    })
  }
  return (
    <div
      className={`master-card m--radius-${visuals?.radius} m--color-${
        visuals?.contentContainerColor
      } m--orientation-${visuals?.imageOrientation} ${
        props.buttons?.length === 1 && props.buttons[0]?.text?.trim() ? 'm--clickable' : ''
      } 
      } ${
        props.buttons?.length === 1 && props.buttons[0]?.text?.trim() && visuals.buttonHide
          ? `m--animation-${visuals?.hoverAnimation}`
          : ''
      } `}
      onClick={() => triggerSingleCta()}
      onMouseDown={(e) => {
        if (singleCtaRef.current) {
          if (singleCtaRef.current.contains(e.target as Node)) return
          setIsPressed(true)
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={
        visuals?.cardSize && visuals.cardSize > 0
          ? {
              height: `${visuals.cardSize}px`,
              flex: '0 0 auto',
            }
          : {
              flex: 1,
            }
      }
    >
      {props.asset && (
        <div
          className={`master-card__asset-container m--color-${visuals?.imageContainerBackground} m--absolute-image-${props.visuals?.imageAbsolute} m--equal-size-${visuals?.equalSize} m--border-around-image-${props.visuals?.imageBorder}`}
        >
          {props.assetLabelText && <span>{props.assetLabelText}</span>}
          <div className={'master-card__asset-inner-container'}>
            <Asset
              altText={props.asset.altText}
              className={`master-card__asset m--ratio-${visuals?.imageRatio} m--absolute-image-${props.visuals?.imageAbsolute} m--border-around-image-${props.visuals?.imageBorder}`}
              objectFit={'cover'}
              thumbnail={props.asset.thumbnail}
            />
            {Array.isArray(badges) && badges.length > 0 && visuals.imageBadgeAvailability && (
              <div className={'master-card__badges'}>{badges}</div>
            )}
          </div>
        </div>
      )}

      <div
        className={`master-card__content-container m--position-${visuals?.positionContent} m--equal-size-${props.visuals?.equalSize}`}
      >
        <div
          className={`master-card__content-inner-container m--position-${visuals?.positionContent} m--padding-${visuals?.contentPadding}`}
        >
          {Array.isArray(badges) && badges.length > 0 && !visuals.imageBadgeAvailability && (
            <div className={'master-card__badges'}>{badges}</div>
          )}
          {props.subtitle && (
            <div
              className={`master-card__subhead m--color-${visuals?.subheadColor} m--size-${visuals?.subheadSize} master-card__text-padding-${visuals?.paddingSubhead}`}
            >
              {props.subtitle}
            </div>
          )}
          {props.headline &&
            (() => {
              const Tag = visuals?.headlineType === 'h3' ? 'h3' : 'div'

              return (
                <Tag
                  className={`master-card__headline m--size-${visuals?.headlineSize} master-card__text-padding-${visuals?.paddingHeadline}`}
                  style={{
                    WebkitLineClamp: visuals?.maxHeadlineLines,
                    lineClamp: visuals?.maxHeadlineLines,
                  }}
                >
                  {props.headline}
                </Tag>
              )
            })()}

          {props.body && (
            <Markdown
              className={`master-card__body  m--size-${visuals?.bodySize} m--list-gap-${visuals?.paddingList} master-card__text-padding-${visuals?.paddingBody} `}
              markdown={props.body}
              style={{
                WebkitLineClamp: visuals?.maxBodyLines,
                lineClamp: visuals?.maxBodyLines,
              }}
            />
          )}
          {props.accordion?.items && props.accordion.items.length > 0 && (
            <div className={'master-card__accordion'}>
              {props.accordion.items.map((item, index) => {
                const isOpen = openAccordionIndex === index
                return (
                  <div className={'master-card__accordion-item'} key={index}>
                    {item.label && (
                      <div
                        className={'master-card__accordion-label-group'}
                        onClick={() => toggleAccordionItem(index)}
                        tabIndex={0}
                      >
                        <div className={'master-card__accordion-label'}>{item.label}</div>
                        <div className={'master-card__accordion-icon'}>
                          <Icon
                            className={`master-card__accordion-arrow ${isOpen ? 'rotated' : ''}`}
                            name={'chevron-down'}
                          />
                        </div>
                      </div>
                    )}
                    {item.markdown && (
                      <AnimateHeight
                        duration={400}
                        easing={'ease-in-out'}
                        height={isOpen ? 'auto' : 0}
                      >
                        <div style={{ paddingLeft: 24 }}>
                          <Markdown
                            className={`master-card__body m--sub-list-padding m--list-gap-3xs m--size-body-sm master-card__text-padding-2xl`}
                            markdown={item.markdown}
                          />
                        </div>
                      </AnimateHeight>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {props.buttons?.filter((btn) => btn.text?.trim()).length > 0 && (
            <div
              className={`master-card__buttons ${
                visuals?.buttonHide ? 'm--is-visually-hidden' : ''
              } ${!hasContentAboveButtons ? 'm--no-padding-top' : ''}`}
            >
              {props.buttons.filter((btn) => btn.text?.trim()).length === 1 ? (
                <div
                  className={`master-card__button m--width-auto m--position-${visuals.positionButton}`}
                  onClick={(e) => {
                    props.onCTAClick?.()
                    const cta = props.buttons.find((btn) => btn.text?.trim())
                    if (cta?.__ctaType !== 'modal') {
                      e.stopPropagation()
                    }
                  }}
                >
                  <CTA
                    className={`coral-global-button m--type-${
                      visuals?.buttonType?.[0] || 'fill'
                    } m--width-${visuals?.buttonWidth} ${
                      isPressed ? 'm--simulate-pressed' : isHovered ? 'm--simulate-hover' : ''
                    } m--theme-${visuals?.buttonTheme} ${
                      visuals.buttonIconDistance ? 'm--icon-distance' : ''
                    } m--size-${visuals?.buttonSize}`}
                    cta={props.buttons.filter((btn) => btn.text?.trim())[0]}
                    iconDirection={visuals.buttonIconDirection}
                    iconName={visuals.buttonIcon}
                    ref={singleCtaRef}
                    type={'clean'}
                  />
                </div>
              ) : (
                <>
                  {props.buttons
                    .filter((btn) => btn.text?.trim())
                    .map((button, index) => (
                      <div
                        className={`master-card__button m--position-${visuals.positionButton}`}
                        key={index}
                      >
                        <CTA
                          className={`coral-global-button m--type-${
                            visuals?.buttonType?.[index] || 'fill'
                          } m--theme-${visuals?.buttonTheme} ${
                            visuals.buttonIconDistance ? 'm--icon-distance' : ''
                          } m--size-${visuals?.buttonSize}`}
                          cta={button}
                          iconDirection={visuals.buttonIconDirection}
                          iconName={visuals.buttonIcon}
                          onClick={() => props.onCTAClick?.()}
                          type={'clean'}
                        />
                      </div>
                    ))}
                </>
              )}
            </div>
          )}

          {props.attributes?.length > 0 && (
            <div className={`master-card__attributes`}>
              {props.attributes.map((attribute, id) => (
                <CoralAttribute
                  icon={attribute.attributeIcon}
                  key={id}
                  label={attribute.attributeLabel}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
